import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Video, VideoOff, Mic, MicOff, Hand, MessageSquare, Users, 
  Maximize, Minimize, Send, X, Check, Volume2, VolumeX,
  Play, Square, AlertCircle, Clock, Power, Edit2, Save,
  Camera, Settings, Loader2, RotateCcw, ChevronDown, ChevronUp
} from 'lucide-react';
import BureauManager from './BureauManager';
import PresentielManager from './PresentielManager';
import { Theta360Viewer, ThetaCameraSelector, ThetaCameraTest, Daily360MeetingRoom, DualCamera360MeetingRoom } from '../video';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGMeetingRoom = ({ 
  selectedAG, 
  user, 
  token, 
  onStartAG, 
  onUpdateAG,
  onRefreshAG,
  hasConfirmedParticipation: propHasConfirmed,
  participationMode: propParticipationMode,
  onParticipationConfirmed,
  onAGClosed,  // CRITICAL: Callback when AG is closed for force disconnect
  isVideoPersistent = false  // If true, video is displayed externally
}) => {
  // Video states
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [showParticipants, setShowParticipants] = useState(false);
  const [handRaised, setHandRaised] = useState(false);
  const [raisedHands, setRaisedHands] = useState([]);
  
  // Chat states
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const chatContainerRef = useRef(null);
  
  // Voting states
  const [activeResolution, setActiveResolution] = useState(null);
  const [votingTimer, setVotingTimer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [userVotes, setUserVotes] = useState({});
  const [votingResults, setVotingResults] = useState({});
  const timerIntervalRef = useRef(null);
  
  // Participation states - use props if provided, otherwise local state
  const [localHasConfirmed, setLocalHasConfirmed] = useState(false);
  const [localParticipationMode, setLocalParticipationMode] = useState(null);
  
  // Use prop values if provided, otherwise use local state
  const hasConfirmedParticipation = propHasConfirmed !== undefined ? propHasConfirmed : localHasConfirmed;
  const participationMode = propParticipationMode !== undefined ? propParticipationMode : localParticipationMode;
  
  const [participants, setParticipants] = useState([]);
  
  // State for collapsible sections
  const [participantsExpanded, setParticipantsExpanded] = useState(true);
  const [chatExpanded, setChatExpanded] = useState(true);
  
  // Bureau state
  const [bureau, setBureau] = useState(null);
  const [showDisconnectMessage, setShowDisconnectMessage] = useState(false);
  
  // 360° THETA state
  const [showStartOptions, setShowStartOptions] = useState(false);
  const [thetaStream, setThetaStream] = useState(null);
  const [thetaCamera, setThetaCamera] = useState(null);
  const [is360Mode, setIs360Mode] = useState(false);
  const [isConnecting360, setIsConnecting360] = useState(false);
  
  // Iframe ref
  const iframeContainerRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Get room URL from AG or generate fallback
  const getRoomUrl = () => {
    // Prefer the room_url stored in the AG (created when manager starts the AG)
    if (selectedAG.room_url) {
      return selectedAG.room_url;
    }
    // Fallback: generate URL (should not happen in normal flow)
    const roomName = `syndicappli-ag-${selectedAG.id}`.replace(/_/g, '-').toLowerCase().substring(0, 40);
    return `https://syndicappli.daily.co/${roomName}`;
  };

  // Load saved votes and participation
  useEffect(() => {
    if (selectedAG?.id && token) {
      loadUserVotes();
      loadParticipants();
      checkParticipation();
      loadChatMessages();
      loadBureau();
      
      // Load voting results for all resolutions immediately
      if (selectedAG.resolutions && selectedAG.resolutions.length > 0) {
        selectedAG.resolutions.forEach(res => {
          loadVotingResults(res.id);
        });
      }
    }
  }, [selectedAG?.id, token]);

  const loadBureau = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/bureau`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBureau(response.data);
    } catch (err) {
      console.log('No bureau set yet');
    }
  };

  // Handle AG closure - disconnect non-bureau participants
  const handleAGClosed = (closeData) => {
    // Check if current user should be disconnected
    const isBureauMember = bureau?.president?.user_id === user?.id ||
      bureau?.scrutateurs?.some(s => s.user_id === user?.id) ||
      bureau?.secretaire?.user_id === user?.id;
    
    if (!isBureauMember && user?.role !== 'gestionnaire') {
      setShowDisconnectMessage(true);
    }
  };

  const handleDisconnectParticipants = (userIds) => {
    // This would ideally use Daily.co API to kick participants
    // For now, we show a message to the user
    if (userIds.includes(user?.id)) {
      setShowDisconnectMessage(true);
    }
  };

  // CRITICAL: Polling for AG closure status - Force disconnect if AG is closed
  useEffect(() => {
    if (!selectedAG?.id || !token) return;
    
    // Poll every 3 seconds to check if AG is closed
    const closureCheckInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/assemblees/${selectedAG.id}/connection-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // CRITICAL: If AG is closed, force disconnect immediately
        if (response.data.is_closed || response.data.must_disconnect) {
          console.log('AG CLOSED - Forcing disconnect');
          setShowDisconnectMessage(true);
          
          // Stop all polling
          clearInterval(closureCheckInterval);
          
          // Notify parent component if callback exists
          if (onAGClosed) {
            onAGClosed(response.data);
          }
        }
      } catch (err) {
        // If we get AG_CLOSED error, show disconnect message
        if (err.response?.data?.detail?.includes('AG_CLOSED')) {
          setShowDisconnectMessage(true);
          clearInterval(closureCheckInterval);
        }
        console.log('Error checking AG closure status:', err);
      }
    }, 3000);
    
    return () => clearInterval(closureCheckInterval);
  }, [selectedAG?.id, token]);

  // Polling for real-time updates - More frequent during active voting
  useEffect(() => {
    if (!selectedAG?.id || !token) return;
    
    // Poll more frequently (every second) when there's an active vote
    const pollDelay = activeResolution ? 1000 : 3000;
    
    const pollInterval = setInterval(async () => {
      loadParticipants();
      loadRaisedHands();
      loadChatMessages();
      
      // CRITICAL: Poll for active resolution to detect when gestionnaire opens a vote
      // This enables coproprietaires to see voting buttons
      try {
        const response = await axios.get(`${API}/assemblees/${selectedAG.id}/active-resolution`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.active && response.data.resolution_id) {
          // A vote is open - update activeResolution state
          if (activeResolution !== response.data.resolution_id) {
            setActiveResolution(response.data.resolution_id);
          }
          // Update time remaining from server
          if (response.data.time_remaining !== null) {
            setTimeRemaining(response.data.time_remaining);
          }
        } else if (!response.data.active && activeResolution) {
          // Vote was closed - clear active resolution
          setActiveResolution(null);
          setTimeRemaining(null);
        }
      } catch (err) {
        console.log('Error polling active resolution:', err);
      }
      
      // Load voting results for ALL resolutions to update summary
      if (selectedAG.resolutions) {
        selectedAG.resolutions.forEach(res => {
          loadVotingResults(res.id);
        });
      }
    }, pollDelay);
    
    return () => clearInterval(pollInterval);
  }, [selectedAG?.id, selectedAG?.resolutions, token, activeResolution]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const loadUserVotes = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/votes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const votes = {};
      response.data.forEach(v => { votes[v.resolution_id] = v.choice; });
      setUserVotes(votes);
    } catch (err) {
      console.log('No votes yet');
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(response.data);
    } catch (err) {
      console.log('Error loading participants');
    }
  };

  const loadRaisedHands = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/raised-hands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRaisedHands(response.data);
    } catch (err) {
      console.log('Error loading raised hands');
    }
  };

  const loadChatMessages = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/chat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(response.data);
    } catch (err) {
      console.log('Error loading chat');
    }
  };

  const loadVotingResults = async (resolutionId) => {
    try {
      const response = await axios.get(`${API}/resolutions/${resolutionId}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVotingResults(prev => ({ ...prev, [resolutionId]: response.data }));
    } catch (err) {
      console.log('Error loading voting results');
    }
  };

  const checkParticipation = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/my-participation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.confirmed) {
        setLocalHasConfirmed(true);
        setLocalParticipationMode(response.data.mode);
        // Notify parent if callback provided
        if (onParticipationConfirmed) {
          onParticipationConfirmed(response.data.mode);
        }
      }
    } catch (err) {
      console.log('No participation yet');
    }
  };

  // Confirm participation
  const confirmParticipation = async (mode) => {
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/participate`, {
        mode: mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocalHasConfirmed(true);
      setLocalParticipationMode(mode);
      // Notify parent if callback provided
      if (onParticipationConfirmed) {
        onParticipationConfirmed(mode);
      }
    } catch (err) {
      alert('Erreur lors de la confirmation de participation');
    }
  };

  // Vote on resolution
  const submitVote = async (resolutionId, choice) => {
    try {
      await axios.post(`${API}/resolutions/${resolutionId}/vote`, {
        choice: choice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserVotes(prev => ({ ...prev, [resolutionId]: choice }));
      loadVotingResults(resolutionId);
    } catch (err) {
      alert('Erreur lors du vote');
    }
  };

  // Start vote with 1 minute timer
  const startVote = async (resolutionId) => {
    try {
      await axios.post(`${API}/resolutions/${resolutionId}/start-vote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActiveResolution(resolutionId);
      setTimeRemaining(60); // 1 minute
      
      // Clear any existing timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      // Start countdown timer
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Auto-close vote when timer reaches 0
            closeVote(resolutionId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Load results periodically during voting
      loadVotingResults(resolutionId);
    } catch (err) {
      alert('Erreur lors de l\'ouverture du vote');
    }
  };

  // Close vote
  const closeVote = async (resolutionId) => {
    try {
      // Clear timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      await axios.post(`${API}/resolutions/${resolutionId}/close-vote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActiveResolution(null);
      setTimeRemaining(null);
      
      // Reload voting results
      loadVotingResults(resolutionId);
    } catch (err) {
      alert('Erreur lors de la clôture du vote');
    }
  };

  // Handle resolution update from VoteCard edit
  const handleResolutionUpdated = (updatedResolution) => {
    // Update the resolution in selectedAG.resolutions (local state)
    if (onUpdateAG && selectedAG) {
      const updatedResolutions = selectedAG.resolutions.map(res => 
        res.id === updatedResolution.id ? { ...res, ...updatedResolution } : res
      );
      onUpdateAG({ ...selectedAG, resolutions: updatedResolutions });
    }
    // Also trigger a full refresh to ensure data consistency
    if (onRefreshAG) {
      onRefreshAG();
    }
  };

  // Vote on behalf of a participant (gestionnaire only)
  const voteForParticipant = async (resolutionId, userId, choice) => {
    try {
      const response = await axios.post(
        `${API}/resolutions/${resolutionId}/vote-for-participant`,
        { user_id: userId, choice: choice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Reload voting results
      loadVotingResults(resolutionId);
      
      return response.data;
    } catch (err) {
      console.error('Error voting for participant:', err);
      throw err;
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Raise/lower hand
  const toggleHand = async () => {
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/raise-hand`, {
        raised: !handRaised
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHandRaised(!handRaised);
    } catch (err) {
      alert('Erreur lors de la demande de parole');
    }
  };

  // Grant speaking permission (gestionnaire only)
  const grantSpeaking = async (userId, grant) => {
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/grant-speaking`, {
        user_id: userId,
        grant: grant
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadRaisedHands();
    } catch (err) {
      alert('Erreur');
    }
  };

  // Send chat message
  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/chat`, {
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      loadChatMessages();
    } catch (err) {
      alert('Erreur envoi message');
    }
  };

  // Fullscreen toggle
  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen && !document.fullscreenElement) {
        await videoContainerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      // Ignore fullscreen errors (user may have exited manually)
      console.log('Fullscreen toggle error:', err.message);
      setIsFullscreen(!!document.fullscreenElement);
    }
  };

  // Open in new window
  const openInNewWindow = () => {
    window.open(getRoomUrl(), '_blank', 'width=1200,height=800,menubar=no,toolbar=no');
  };

  // If AG not started
  if (selectedAG.status !== 'En cours' && selectedAG.status !== 'Ouverte') {
    return (
      <div className="space-y-6">
        {/* Participation confirmation for coproprietaires */}
        {user?.role === 'coproprietaire' && selectedAG.status === 'Publiée' && !hasConfirmedParticipation && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmez votre participation</h3>
            <p className="text-gray-600 mb-6">
              Pour être inscrit sur la feuille de présence et pouvoir voter, veuillez confirmer votre mode de participation :
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => confirmParticipation('video')}
                className="p-4 border-2 border-[#00D3A8] bg-[#00D3A8]/5 rounded-xl hover:bg-[#00D3A8]/10 transition-all text-center"
                data-testid="participate-video-btn"
              >
                <Video className="mx-auto text-[#00D3A8] mb-2" size={32} />
                <span className="font-semibold text-gray-800 block">Je participe en vidéo</span>
                <span className="text-sm text-gray-500">Caméra et micro</span>
              </button>
              
              <button
                onClick={() => confirmParticipation('audio')}
                className="p-4 border-2 border-blue-500 bg-blue-50 rounded-xl hover:bg-blue-100 transition-all text-center"
                data-testid="participate-audio-btn"
              >
                <Mic className="mx-auto text-blue-500 mb-2" size={32} />
                <span className="font-semibold text-gray-800 block">Je participe en audio</span>
                <span className="text-sm text-gray-500">Micro uniquement</span>
              </button>
              
              <button
                onClick={() => confirmParticipation('presentiel')}
                className="p-4 border-2 border-purple-500 bg-purple-50 rounded-xl hover:bg-purple-100 transition-all text-center"
                data-testid="participate-presentiel-btn"
              >
                <Users className="mx-auto text-purple-500 mb-2" size={32} />
                <span className="font-semibold text-gray-800 block">Je serai présent en salle</span>
                <span className="text-sm text-gray-500">Participation physique</span>
              </button>
              
              <button
                onClick={() => confirmParticipation('correspondance')}
                className="p-4 border-2 border-amber-500 bg-amber-50 rounded-xl hover:bg-amber-100 transition-all text-center"
                data-testid="participate-correspondance-btn"
              >
                <MessageSquare className="mx-auto text-amber-500 mb-2" size={32} />
                <span className="font-semibold text-gray-800 block">Vote par correspondance</span>
                <span className="text-sm text-gray-500">Je vote à distance</span>
              </button>
            </div>
            
            {/* Delegation option - full width */}
            <div className="mt-4">
              <button
                onClick={() => confirmParticipation('delegation')}
                className="w-full p-4 border-2 border-gray-400 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all text-center"
                data-testid="participate-delegation-btn"
              >
                <Users className="mx-auto text-gray-500 mb-2" size={32} />
                <span className="font-semibold text-gray-800 block">Je délègue mon pouvoir</span>
                <span className="text-sm text-gray-500">Procuration à un autre coproprietaire</span>
              </button>
            </div>
          </div>
        )}

        {/* Participation confirmed */}
        {user?.role === 'coproprietaire' && hasConfirmedParticipation && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
            <Check className="text-green-500 flex-shrink-0" size={24} />
            <div>
              <span className="font-semibold text-green-800">Participation confirmée</span>
              <span className="text-green-700 text-sm block">
                Mode : {participationMode === 'video' ? 'Vidéo' : 
                        participationMode === 'audio' ? 'Audio' :
                        participationMode === 'presentiel' ? 'Présentiel (en salle)' :
                        participationMode === 'correspondance' ? 'Vote par correspondance' : 'Délégation'}
              </span>
            </div>
          </div>
        )}

        {/* Waiting message */}
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-gray-500">
            <p className="text-lg font-medium mb-2">Salle de réunion non disponible</p>
            <p className="text-sm">
              {selectedAG.status === 'Publiée' && "L'AG n'a pas encore été démarrée par le gestionnaire"}
              {selectedAG.status === 'Passée' && "L'AG est terminée"}
            </p>
            {user?.role === 'gestionnaire' && selectedAG.status === 'Publiée' && !showStartOptions && (
              <button
                onClick={() => setShowStartOptions(true)}
                className="mt-4 bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-3 px-8 rounded-xl transition-colors flex items-center space-x-2 mx-auto"
                data-testid="start-ag-btn"
              >
                <Play size={20} />
                <span>Démarrer l'AG maintenant</span>
              </button>
            )}
          </div>
        </div>

        {/* START OPTIONS PANEL - 360° vs Standard - More compact */}
        {user?.role === 'gestionnaire' && selectedAG.status === 'Publiée' && showStartOptions && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100" data-testid="start-options-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="text-[#00D3A8]" size={18} />
                Mode de visioconférence
              </h3>
              <button
                onClick={() => setShowStartOptions(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            {/* Two-column layout: Camera selector + Test on left, Start buttons on right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left side: Camera selection and test */}
              <div className="space-y-3">
                {/* Camera selector - more compact */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <ThetaCameraSelector
                    onCameraSelected={(info) => {
                      setThetaCamera(info);
                      setIs360Mode(info.isTheta);
                    }}
                    onStreamReady={(stream, isTheta) => {
                      setThetaStream(stream);
                      setIs360Mode(isTheta);
                    }}
                  />
                </div>

                {/* Camera Test - NEW: Shows when THETA is connected */}
                {thetaStream && is360Mode && (
                  <ThetaCameraTest 
                    stream={thetaStream}
                    isTheta={is360Mode}
                    cameraInfo={thetaCamera}
                    onTestComplete={(passed, results) => {
                      console.log('Camera test:', passed ? 'PASSED' : 'FAILED', results);
                    }}
                  />
                )}
              </div>

              {/* Right side: Start buttons */}
              <div className="space-y-3">
                {/* Start buttons - stacked vertically for better UX */}
                <button
                  onClick={() => {
                    if (thetaStream) {
                      thetaStream.getTracks().forEach(track => track.stop());
                      setThetaStream(null);
                    }
                    onStartAG('standard');
                  }}
                  disabled={isConnecting360}
                  className="w-full flex items-center p-4 rounded-xl border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
                  data-testid="btn-start-ag-standard"
                >
                  <div className="p-3 bg-blue-100 rounded-full mr-4 group-hover:bg-blue-200 transition-colors">
                    <Video className="text-blue-600" size={24} />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-gray-900 block">Démarrer l'AG</span>
                    <span className="text-sm text-gray-500">Visioconférence standard</span>
                  </div>
                </button>

                <button
                  onClick={async () => {
                    setIsConnecting360(true);
                    try {
                      await onStartAG('360', thetaStream);
                    } finally {
                      setIsConnecting360(false);
                    }
                  }}
                  disabled={isConnecting360 || !is360Mode}
                  className={`w-full flex items-center p-4 rounded-xl border-2 transition-all group ${
                    is360Mode
                      ? 'border-[#00D3A8]/30 hover:border-[#00D3A8] hover:bg-[#00D3A8]/5'
                      : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                  data-testid="btn-start-ag-360"
                >
                  <div className={`p-3 rounded-full mr-4 transition-colors ${
                    is360Mode
                      ? 'bg-[#00D3A8]/20 group-hover:bg-[#00D3A8]/30'
                      : 'bg-gray-200'
                  }`}>
                    {isConnecting360 ? (
                      <Loader2 className="text-[#00D3A8] animate-spin" size={24} />
                    ) : (
                      <Camera className={is360Mode ? 'text-[#00D3A8]' : 'text-gray-400'} size={24} />
                    )}
                  </div>
                  <div className="text-left">
                    <span className={`font-semibold block ${is360Mode ? 'text-gray-900' : 'text-gray-400'}`}>
                      Démarrer l'AG 360°
                    </span>
                    <span className={`text-sm ${is360Mode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isConnecting360 
                        ? 'Connexion en cours...'
                        : is360Mode 
                          ? `RICOH ${thetaCamera?.model || 'THETA'} prête` 
                          : 'Connectez une RICOH THETA'}
                    </span>
                  </div>
                </button>

                {/* Compact instructions - collapsible */}
                <details className="p-3 bg-[#00D3A8]/5 rounded-lg border border-[#00D3A8]/20">
                  <summary className="font-medium text-gray-800 text-sm cursor-pointer flex items-center gap-2">
                    <Camera className="text-[#00D3A8]" size={14} />
                    Comment utiliser la visio 360° ?
                  </summary>
                  <ol className="text-xs text-gray-600 space-y-1 mt-2 ml-5">
                    <li>1. Connectez votre RICOH THETA via USB</li>
                    <li>2. Activez le mode "Live Streaming"</li>
                    <li>3. Sélectionnez la caméra ci-dessus</li>
                    <li>4. Vérifiez le test, puis démarrez</li>
                  </ol>
                  <p className="text-xs text-gray-500 mt-2 ml-5">
                    Compatible: THETA X, Z1, SC2
                  </p>
                </details>
              </div>
            </div>
          </div>
        )}

        {/* Vote par correspondance */}
        {user?.role === 'coproprietaire' && participationMode === 'correspondance' && selectedAG.resolutions?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Vote par correspondance</h3>
            <p className="text-gray-600 mb-6 text-sm">
              Votez sur chaque résolution ci-dessous. Vos votes seront comptabilisés lors de l'assemblée.
            </p>
            
            <div className="space-y-4">
              {selectedAG.resolutions.map((resolution) => (
                <VoteCard 
                  key={resolution.id}
                  resolution={resolution}
                  userVote={userVotes[resolution.id]}
                  onVote={(choice) => submitVote(resolution.id, choice)}
                  showResults={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // AG is live - show meeting room
  return (
    <div className={`space-y-3 ${isFullscreen ? 'fixed inset-0 z-50 bg-gray-900 p-4' : ''}`}>
      
      {/* Raised Hands Alert Panel for Gestionnaire - Compact */}
      {user?.role === 'gestionnaire' && raisedHands.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Hand className="text-amber-500" size={16} />
              <span className="font-medium text-amber-800 text-sm">
                {raisedHands.length} demande{raisedHands.length > 1 ? 's' : ''} de parole
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {raisedHands.map((hand) => (
                <div key={hand.user_id} className="flex items-center space-x-1 bg-white px-2 py-1 rounded border border-amber-200">
                  <span className="text-xs font-medium text-gray-700">{hand.user_name}</span>
                  <button
                    onClick={() => grantSpeaking(hand.user_id, true)}
                    className="text-green-600 hover:bg-green-100 p-1 rounded"
                    title="Accepter"
                  >
                    <Mic size={12} />
                  </button>
                  <button
                    onClick={() => grantSpeaking(hand.user_id, false)}
                    className="text-red-600 hover:bg-red-100 p-1 rounded"
                    title="Refuser"
                  >
                    <MicOff size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Participants Bar - Collapsible */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <button 
          onClick={() => setParticipantsExpanded(!participantsExpanded)}
          className="w-full flex items-center justify-between p-2 hover:bg-gray-50"
        >
          <h3 className="font-medium text-gray-800 flex items-center space-x-2 text-sm">
            <Users size={16} className="text-[#00D3A8]" />
            <span>Participants ({participants.length})</span>
          </h3>
          <div className="flex items-center space-x-2">
            {user?.role === 'gestionnaire' && (
              <div onClick={(e) => e.stopPropagation()}>
                <PresentielManager 
                  assemblee={selectedAG}
                  token={token}
                  onParticipantAdded={() => loadParticipants()}
                />
              </div>
            )}
            {participantsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </div>
        </button>
        
        {participantsExpanded && (
          <div className="px-3 pb-2 flex flex-wrap gap-1.5">
            {participants.length > 0 ? (
              participants.map((p) => (
                <div 
                  key={p.id} 
                  className={`flex items-center space-x-1 px-1.5 py-0.5 rounded border text-xs ${
                    p.is_animateur 
                      ? 'border-blue-400 bg-blue-50' 
                      : p.mode === 'presentiel'
                        ? 'bg-purple-50 border-purple-200'
                        : p.mode === 'correspondance'
                          ? 'bg-blue-50 border-blue-200'
                          : p.hand_raised 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-gray-50 border-gray-200'
                  } ${p.user_id === user?.id ? 'ring-1 ring-[#00D3A8]' : ''}`}
                >
                  {/* Avatar mini */}
                  <div className="relative">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium ${
                      p.is_animateur ? 'bg-blue-600' : 
                      p.mode === 'presentiel' ? 'bg-purple-500' :
                      p.mode === 'correspondance' ? 'bg-blue-400' :
                      p.hand_raised ? 'bg-amber-500' : 'bg-[#00D3A8]'
                    }`}>
                      {p.prenom?.[0]}{p.nom?.[0]}
                    </div>
                    {p.is_animateur && (
                      <div className="absolute -top-0.5 -right-0.5 text-[8px]">👑</div>
                    )}
                  </div>
                  
                  {/* Mode icon + Name */}
                  <span className="text-[10px]">
                    {p.is_animateur && '🎬'}
                    {!p.is_animateur && p.mode === 'video' && '📹'}
                    {!p.is_animateur && p.mode === 'audio' && '🎤'}
                    {!p.is_animateur && p.mode === 'correspondance' && '📧'}
                    {!p.is_animateur && p.mode === 'delegation' && '👥'}
                    {!p.is_animateur && p.mode === 'presentiel' && '🏢'}
                  </span>
                  <span className="font-medium text-gray-700 truncate max-w-[60px]">
                    {p.prenom} {p.nom?.[0]}.
                  </span>
                  {p.user_id === user?.id && (
                    <span className="text-[9px] text-[#00D3A8]">(vous)</span>
                  )}
                  
                  {/* Hand raised */}
                  {p.hand_raised && <Hand size={10} className="text-amber-500 animate-bounce" />}
                  
                  {/* Grant speaking button - compact */}
                  {user?.role === 'gestionnaire' && p.hand_raised && !p.is_animateur && (
                    <button
                      onClick={() => grantSpeaking(p.user_id, true)}
                      className="text-[9px] bg-green-500 hover:bg-green-600 text-white px-1 py-0.5 rounded flex items-center"
                    >
                      <Mic size={8} />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-400 py-1">
                Aucun participant
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disconnect Message Modal for remote participants when AG is closed */}
      {showDisconnectMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Power className="text-amber-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Assemblée Générale Clôturée</h3>
            <p className="text-gray-600 mb-4">
              Le gestionnaire a clôturé l'Assemblée Générale. 
              Merci de votre participation.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Le procès-verbal sera disponible prochainement.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-3 px-6 rounded-lg transition-colors"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      )}

      {/* Bureau Management - Only for gestionnaire */}
      {user?.role === 'gestionnaire' && (selectedAG.status === 'En cours' || selectedAG.status === 'Ouverte') && (
        <BureauManager
          selectedAG={selectedAG}
          token={token}
          participants={participants}
          onBureauUpdated={setBureau}
          onRefreshAG={onRefreshAG}
        />
      )}

      {/* Video Controls Bar - Always visible at top */}
      {!isVideoPersistent && selectedAG.room_url && (
        <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-white">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Réunion en cours</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              title={isFullscreen ? "Quitter plein écran" : "Plein écran"}
            >
              {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
            
            <button
              onClick={openInNewWindow}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              title="Ouvrir dans une nouvelle fenêtre"
            >
              <Video size={20} />
            </button>
            
            {/* Raise hand button */}
            <button
              onClick={toggleHand}
              className={`p-2 rounded-full transition-colors ${
                handRaised ? 'bg-amber-500 text-white' : 'text-white hover:bg-white/20'
              }`}
              title={handRaised ? "Baisser la main" : "Lever la main"}
              data-testid="raise-hand-btn"
            >
              <Hand size={20} />
            </button>
            
            <button
              onClick={() => setShowChat(!showChat)}
              className={`p-2 rounded-full transition-colors ${
                showChat ? 'bg-[#00D3A8] text-white' : 'text-white hover:bg-white/20'
              }`}
              title="Afficher/masquer le chat"
            >
              <MessageSquare size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Main Content Layout - Chat as horizontal banner, then Resolutions + Summary */}
      
      {/* Chat Banner - Collapsible horizontal strip */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <button 
          onClick={() => setChatExpanded(!chatExpanded)}
          className="w-full flex items-center justify-between p-2 border-b border-gray-100 hover:bg-gray-50"
        >
          <h3 className="font-medium text-gray-800 flex items-center space-x-2 text-sm">
            <MessageSquare size={14} className="text-[#00D3A8]" />
            <span>Chat en direct</span>
            <span className="text-xs text-gray-400">({chatMessages.length})</span>
          </h3>
          {chatExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>
        
        {chatExpanded && (
          <div className="flex items-start">
            {/* Messages display - horizontal scrollable */}
            <div 
              ref={chatContainerRef}
              className="flex-1 overflow-x-auto overflow-y-hidden p-2 flex space-x-2 h-16"
              style={{ scrollbarWidth: 'thin' }}
            >
              {chatMessages.length === 0 ? (
                <p className="text-gray-400 text-xs py-2 w-full text-center">Aucun message</p>
              ) : (
                chatMessages.slice(-15).map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`flex-shrink-0 max-w-[200px] px-2 py-1.5 rounded-lg ${
                      msg.sender_id === user?.id ? 'bg-[#00D3A8] text-white' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {msg.sender_id !== user?.id && (
                      <p className="text-[10px] font-semibold mb-0.5 truncate">{msg.sender_name}</p>
                    )}
                    <p className="text-xs line-clamp-2">{msg.content}</p>
                  </div>
                ))
              )}
            </div>
            
            {/* Chat input - always visible */}
            <form onSubmit={sendChatMessage} className="flex-shrink-0 p-2 border-l border-gray-100 flex space-x-1">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Message..."
                className="w-36 px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#00D3A8]"
                data-testid="chat-input"
              />
              <button
                type="submit"
                className="p-1.5 bg-[#00D3A8] text-white rounded hover:bg-[#00C7A1]"
              >
                <Send size={14} />
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Resolutions (3/4) + Results Summary (1/4) */}
      <div className="grid gap-3 lg:grid-cols-4">
        {/* Resolutions Column (3/4) */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-base">Résolutions à voter</h3>
              {activeResolution && timeRemaining !== null && (
                <span className={`text-sm font-bold px-2 py-1 rounded-full flex items-center space-x-1 ${
                  timeRemaining <= 10 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-blue-100 text-blue-700'
                }`}>
                  <Clock size={12} />
                  <span>⏱️ {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</span>
                </span>
              )}
            </div>
            
            <div className="space-y-3 max-h-[calc(100vh-420px)] min-h-[200px] overflow-y-auto pr-1">
              {selectedAG.resolutions?.length > 0 ? (
                selectedAG.resolutions.map((resolution) => (
                  <VoteCard 
                    key={resolution.id}
                    resolution={resolution}
                    userVote={userVotes[resolution.id]}
                    onVote={(choice) => submitVote(resolution.id, choice)}
                    showResults={user?.role === 'gestionnaire'}
                    results={votingResults[resolution.id]}
                    userRole={user?.role}
                    isActive={activeResolution === resolution.id}
                    timeRemaining={activeResolution === resolution.id ? timeRemaining : null}
                    onStartVote={startVote}
                    onCloseVote={closeVote}
                    totalVoters={participants.length}
                    token={token}
                    onResolutionUpdated={handleResolutionUpdated}
                    participants={participants}
                    onVoteForParticipant={voteForParticipant}
                    assemblee={selectedAG}
                  />
                ))
              ) : (
                <p className="text-center text-gray-400 text-sm py-6">Aucune résolution à voter</p>
              )}
            </div>
          </div>
        </div>

        {/* Results Summary Column (1/4) */}
        <div className="lg:col-span-1">
          <VoteResultsSummary 
            resolutions={selectedAG.resolutions || []}
            votingResults={votingResults}
            totalVoters={participants.length}
            userRole={user?.role}
            activeResolutionId={activeResolution}
          />
        </div>
      </div>
    </div>
  );
};

// Vote card component with pie chart and timer
const VoteCard = ({ resolution, userVote, onVote, showResults, results, userRole, isActive, timeRemaining, onStartVote, onCloseVote, totalVoters, isGreyedOut, token, onResolutionUpdated, participants, onVoteForParticipant, assemblee }) => {
  const cardRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitre, setEditedTitre] = useState(resolution.titre || '');
  const [editedDescription, setEditedDescription] = useState(resolution.description || '');
  const [editedMajorite, setEditedMajorite] = useState(resolution.type_majorite || 'Simple - Art. 24');
  const [savingEdit, setSavingEdit] = useState(false);
  const [showVoteManager, setShowVoteManager] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [allVoters, setAllVoters] = useState([]);
  const [loadingVoters, setLoadingVoters] = useState(false);
  const [voterFilter, setVoterFilter] = useState('all');

  // Load all voters when vote manager is opened
  useEffect(() => {
    if (showVoteManager && assemblee?.id && userRole === 'gestionnaire') {
      loadAllVoters();
    }
  }, [showVoteManager, assemblee?.id]);

  const loadAllVoters = async () => {
    setLoadingVoters(true);
    try {
      const response = await axios.get(`${API}/assemblees/${assemblee.id}/all-voters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllVoters(response.data);
    } catch (error) {
      console.error('Error loading voters:', error);
      // Fallback to participants if endpoint fails
      setAllVoters(participants.map(p => ({
        user_id: p.user_id,
        nom: p.nom,
        prenom: p.prenom,
        mode: p.mode,
        can_vote: true,
        status: p.mode || 'online'
      })));
    } finally {
      setLoadingVoters(false);
    }
  };

  const filteredVoters = allVoters.filter(v => {
    if (voterFilter === 'all') return true;  // Show all coproprietaires
    if (voterFilter === 'presentiel') return v.mode === 'presentiel';
    if (voterFilter === 'online') return v.mode && ['video', 'audio'].includes(v.mode);
    return true;
  });
  
  const getVoteButtonClass = (choice) => {
    const base = "flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center space-x-1";
    
    if (userVote === choice) {
      if (choice === 'pour') return `${base} bg-green-500 text-white`;
      if (choice === 'contre') return `${base} bg-red-500 text-white`;
      if (choice === 'abstention') return `${base} bg-gray-500 text-white`;
    }
    
    if (choice === 'pour') return `${base} border-2 border-green-500 text-green-600 hover:bg-green-50`;
    if (choice === 'contre') return `${base} border-2 border-red-500 text-red-600 hover:bg-red-50`;
    return `${base} border-2 border-gray-400 text-gray-600 hover:bg-gray-50`;
  };

  const isVotingClosed = resolution.resultat !== 'En attente';
  const canVote = userRole === 'coproprietaire' && !isVotingClosed && isActive;

  // Auto-scroll when this card becomes active
  useEffect(() => {
    if (isActive && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActive]);

  // Calculate percentages for pie chart with tantièmes
  const getPieChartData = () => {
    if (!results || !results.results) return null;
    const resData = results.results;
    const pour = resData.pour?.count || 0;
    const contre = resData.contre?.count || 0;
    const abstention = resData.abstention?.count || 0;
    const total = pour + contre + abstention;
    
    // Get tantièmes
    const pourTantiemes = resData.pour?.tantiemes || 0;
    const contreTantiemes = resData.contre?.tantiemes || 0;
    const abstentionTantiemes = resData.abstention?.tantiemes || 0;
    const totalTantiemes = pourTantiemes + contreTantiemes + abstentionTantiemes;
    
    if (total === 0) return null;
    
    const pourPct = Math.round((pour / total) * 100);
    const contrePct = Math.round((contre / total) * 100);
    const abstPct = 100 - pourPct - contrePct;
    
    // Percentage based on tantièmes
    const pourTantPct = totalTantiemes > 0 ? Math.round((pourTantiemes / totalTantiemes) * 100) : 0;
    const contreTantPct = totalTantiemes > 0 ? Math.round((contreTantiemes / totalTantiemes) * 100) : 0;
    const abstTantPct = totalTantiemes > 0 ? 100 - pourTantPct - contreTantPct : 0;
    
    return { 
      pour: pourPct, 
      contre: contrePct, 
      abstention: abstPct, 
      total, 
      counts: { pour, contre, abstention },
      tantiemes: { pour: pourTantiemes, contre: contreTantiemes, abstention: abstentionTantiemes, total: totalTantiemes },
      tantPct: { pour: pourTantPct, contre: contreTantPct, abstention: abstTantPct }
    };
  };

  const pieData = getPieChartData();

  // Save resolution edits
  const saveResolutionEdits = async () => {
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/resolutions/${resolution.id}`,
        {
          titre: editedTitre,
          description: editedDescription,
          type_majorite: editedMajorite
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsEditing(false);
      if (onResolutionUpdated) {
        onResolutionUpdated({
          ...resolution,
          titre: editedTitre,
          description: editedDescription,
          type_majorite: editedMajorite
        });
      }
    } catch (error) {
      console.error('Error updating resolution:', error);
      alert('Erreur lors de la mise à jour de la résolution');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle vote for participant
  const handleVoteForParticipant = async (userId, choice) => {
    if (!onVoteForParticipant) return;
    setVotingInProgress(true);
    try {
      await onVoteForParticipant(resolution.id, userId, choice);
      setSelectedParticipant(null);
    } catch (error) {
      alert('Erreur lors du vote: ' + (error.response?.data?.detail || error.message));
    } finally {
      setVotingInProgress(false);
    }
  };

  // SVG Pie Chart component
  const PieChart = ({ pour, contre, abstention }) => {
    // Calculate stroke-dasharray for each segment
    const circumference = 2 * Math.PI * 40; // radius = 40
    const pourLength = (pour / 100) * circumference;
    const contreLength = (contre / 100) * circumference;
    const abstLength = (abstention / 100) * circumference;
    
    let offset = 0;
    const pourOffset = offset;
    offset += pourLength;
    const contreOffset = offset;
    offset += contreLength;
    const abstOffset = offset;

    return (
      <svg width="100" height="100" viewBox="0 0 100 100" className="transform -rotate-90">
        {/* Pour - Green */}
        {pour > 0 && (
          <circle
            cx="50" cy="50" r="40"
            fill="none"
            stroke="#22c55e"
            strokeWidth="20"
            strokeDasharray={`${pourLength} ${circumference}`}
            strokeDashoffset={-pourOffset}
          />
        )}
        {/* Contre - Red */}
        {contre > 0 && (
          <circle
            cx="50" cy="50" r="40"
            fill="none"
            stroke="#ef4444"
            strokeWidth="20"
            strokeDasharray={`${contreLength} ${circumference}`}
            strokeDashoffset={-contreOffset}
          />
        )}
        {/* Abstention - Gray */}
        {abstention > 0 && (
          <circle
            cx="50" cy="50" r="40"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="20"
            strokeDasharray={`${abstLength} ${circumference}`}
            strokeDashoffset={-abstOffset}
          />
        )}
        {/* Center circle for donut effect */}
        <circle cx="50" cy="50" r="30" fill="white" />
      </svg>
    );
  };

  // Format time remaining
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      ref={cardRef}
      className={`p-4 border rounded-xl transition-all ${
        isActive ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200' :
        isVotingClosed ? 'bg-gray-100 border-gray-300 opacity-60' : 
        'border-gray-200 hover:border-[#00D3A8]'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className={`text-white text-xs font-bold px-2 py-1 rounded ${
            isVotingClosed ? 'bg-gray-400' : 'bg-[#00D3A8]'
          }`}>
            R{resolution.numero || resolution.ordre}
          </span>
          <span className="text-xs text-gray-500">{isEditing ? editedMajorite : (resolution.type_majorite || resolution.majorite)}</span>
          {/* Edit button for gestionnaire */}
          {userRole === 'gestionnaire' && !isVotingClosed && !isActive && !isEditing && (
            <button
              onClick={() => {
                setEditedTitre(resolution.titre || '');
                setEditedDescription(resolution.description || '');
                setEditedMajorite(resolution.type_majorite || 'Simple - Art. 24');
                setIsEditing(true);
              }}
              className="text-gray-400 hover:text-[#00D3A8] transition-colors p-1"
              title="Modifier la résolution"
            >
              <Edit2 size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* Timer display when vote is active */}
          {isActive && timeRemaining !== null && (
            <span className={`text-sm font-bold px-2 py-1 rounded-full ${
              timeRemaining <= 10 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-blue-100 text-blue-700'
            }`}>
              ⏱️ {formatTime(timeRemaining)}
            </span>
          )}
          {isVotingClosed && (
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              resolution.resultat === 'Adoptée' ? 'bg-green-100 text-green-700' :
              resolution.resultat === 'Rejetée' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {resolution.resultat}
            </span>
          )}
        </div>
      </div>
      
      {/* Edit Form */}
      {isEditing ? (
        <div className="space-y-3 mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Titre</label>
            <input
              type="text"
              value={editedTitre}
              onChange={(e) => setEditedTitre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              placeholder="Titre de la résolution"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Description</label>
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              rows={2}
              placeholder="Description de la résolution"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Majorité requise</label>
            <select
              value={editedMajorite}
              onChange={(e) => setEditedMajorite(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
            >
              <option value="Simple - Art. 24">Art. 24 - Majorité Simple</option>
              <option value="Absolue - Art. 25">Art. 25 - Majorité Absolue</option>
              <option value="Passerelle - Art. 25-1">Art. 25-1 - Passerelle</option>
              <option value="Double - Art. 26">Art. 26 - Double Majorité</option>
              <option value="Unanimité">Unanimité</option>
            </select>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={saveResolutionEdits}
              disabled={savingEdit}
              className="flex-1 py-2 px-3 bg-[#00D3A8] hover:bg-[#00C7A1] text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-1 disabled:opacity-50"
            >
              {savingEdit ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Save size={14} />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="py-2 px-3 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <>
          <h4 className="font-medium text-gray-800 text-sm mb-2">{resolution.titre}</h4>
          
          {resolution.description && (
            <p className="text-xs text-gray-500 mb-3 line-clamp-2">{resolution.description}</p>
          )}
        </>
      )}
      
      {/* Gestionnaire controls - Start/Close vote */}
      {userRole === 'gestionnaire' && !isVotingClosed && (
        <div className="flex space-x-2 mb-3">
          {!isActive ? (
            <button
              onClick={() => onStartVote && onStartVote(resolution.id)}
              className="flex-1 py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-2"
              data-testid={`start-vote-${resolution.id}`}
            >
              <Play size={16} />
              <span>Ouvrir le vote (1 min)</span>
            </button>
          ) : (
            <>
              <button
                onClick={() => onCloseVote && onCloseVote(resolution.id)}
                className="flex-1 py-2 px-3 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-2"
                data-testid={`close-vote-${resolution.id}`}
              >
                <Square size={16} />
                <span>Clôturer le vote</span>
              </button>
              <button
                onClick={() => setShowVoteManager(!showVoteManager)}
                className={`py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center space-x-1 ${
                  showVoteManager 
                    ? 'bg-amber-500 text-white' 
                    : 'border border-amber-500 text-amber-600 hover:bg-amber-50'
                }`}
                data-testid={`vote-manager-${resolution.id}`}
                title="Voter pour un participant"
              >
                <Users size={16} />
                <span>Modifier votes</span>
              </button>
            </>
          )}
        </div>
      )}
      
      {/* Vote Manager Panel - Gestionnaire can vote for participants */}
      {showVoteManager && userRole === 'gestionnaire' && isActive && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <h5 className="font-medium text-amber-800 text-sm mb-2 flex items-center space-x-1">
            <Users size={14} />
            <span>Voter pour un coproprietaire</span>
          </h5>
          
          {/* Filter tabs */}
          <div className="flex space-x-1 mb-2">
            <button 
              onClick={() => setVoterFilter('all')}
              className={`px-2 py-1 text-xs rounded ${voterFilter === 'all' ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 border border-amber-300'}`}
            >
              Tous ({allVoters.length})
            </button>
            <button 
              onClick={() => setVoterFilter('presentiel')}
              className={`px-2 py-1 text-xs rounded ${voterFilter === 'presentiel' ? 'bg-purple-500 text-white' : 'bg-white text-purple-700 border border-purple-300'}`}
            >
              Présentiels ({allVoters.filter(v => v.mode === 'presentiel').length})
            </button>
            <button 
              onClick={() => setVoterFilter('online')}
              className={`px-2 py-1 text-xs rounded ${voterFilter === 'online' ? 'bg-green-500 text-white' : 'bg-white text-green-700 border border-green-300'}`}
            >
              En ligne ({allVoters.filter(v => v.mode && ['video', 'audio'].includes(v.mode)).length})
            </button>
          </div>
          
          {loadingVoters ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500 mx-auto"></div>
              <p className="text-xs text-amber-600 mt-2">Chargement...</p>
            </div>
          ) : filteredVoters.length === 0 ? (
            <p className="text-xs text-amber-600 text-center py-2">Aucun coproprietaire dans cette catégorie</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {filteredVoters.map((voter) => (
                <div 
                  key={voter.user_id}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    selectedParticipant === voter.user_id 
                      ? 'border-amber-500 bg-amber-100' 
                      : voter.is_present
                        ? 'border-green-200 bg-green-50 hover:bg-green-100'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => setSelectedParticipant(selectedParticipant === voter.user_id ? null : voter.user_id)}
                    className="flex-1 text-left text-sm"
                  >
                    <span className="font-medium">{voter.prenom} {voter.nom}</span>
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                      voter.mode === 'presentiel' 
                        ? 'bg-purple-100 text-purple-700' 
                        : voter.mode === 'correspondance'
                          ? 'bg-blue-100 text-blue-700'
                          : voter.mode === 'video' || voter.mode === 'audio'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                    }`}>
                      {voter.mode === 'presentiel' ? '🏢 Présentiel' : 
                       voter.mode === 'correspondance' ? '📧 VPC' : 
                       voter.mode === 'video' ? '📹 Vidéo' :
                       voter.mode === 'audio' ? '🎤 Audio' : 
                       voter.mode === 'absent' || !voter.mode ? '⬜ Absent' : voter.mode}
                    </span>
                    {voter.tantiemes > 0 && (
                      <span className="ml-1 text-xs text-gray-500">({voter.tantiemes}t)</span>
                    )}
                  </button>
                  {selectedParticipant === voter.user_id && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleVoteForParticipant(voter.user_id, 'Pour')}
                        disabled={votingInProgress}
                        className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                      >
                        Pour
                      </button>
                      <button
                        onClick={() => handleVoteForParticipant(voter.user_id, 'Contre')}
                        disabled={votingInProgress}
                        className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                      >
                        Contre
                      </button>
                      <button
                        onClick={() => handleVoteForParticipant(voter.user_id, 'Abstention')}
                        disabled={votingInProgress}
                        className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:opacity-50"
                      >
                        Abst.
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Vote buttons */}
      {canVote && (isActive || !showResults) && (
        <div className="flex space-x-2 mt-3" data-testid={`vote-buttons-${resolution.id}`}>
          <button
            onClick={() => onVote('pour')}
            className={getVoteButtonClass('pour')}
            data-testid={`vote-pour-${resolution.id}`}
          >
            <Check size={16} />
            <span>POUR</span>
          </button>
          <button
            onClick={() => onVote('contre')}
            className={getVoteButtonClass('contre')}
            data-testid={`vote-contre-${resolution.id}`}
          >
            <X size={16} />
            <span>CONTRE</span>
          </button>
          <button
            onClick={() => onVote('abstention')}
            className={getVoteButtonClass('abstention')}
            data-testid={`vote-abstention-${resolution.id}`}
          >
            <span>ABST.</span>
          </button>
        </div>
      )}
      
      {/* Show user's vote */}
      {userVote && (
        <div className="mt-2 text-xs text-gray-500">
          Votre vote : <span className="font-semibold capitalize">{userVote}</span>
        </div>
      )}
      
      {/* Results with pie chart for gestionnaire or after voting closed */}
      {(showResults || isVotingClosed) && results && pieData && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            {/* Pie Chart */}
            <div className="flex-shrink-0">
              <PieChart pour={pieData.pour || 0} contre={pieData.contre || 0} abstention={pieData.abstention || 0} />
            </div>
            
            {/* Vote counts and percentages with tantièmes */}
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Pour</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-green-600">{pieData?.counts?.pour || 0}</span>
                  <span className="text-gray-500 text-sm ml-1">({pieData?.pour || 0}%)</span>
                  <span className="text-green-600 text-xs ml-2">{pieData?.tantiemes?.pour || 0} t.</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Contre</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-red-600">{pieData?.counts?.contre || 0}</span>
                  <span className="text-gray-500 text-sm ml-1">({pieData?.contre || 0}%)</span>
                  <span className="text-red-600 text-xs ml-2">{pieData?.tantiemes?.contre || 0} t.</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <span className="text-sm text-gray-700">Abstention</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-600">{pieData?.counts?.abstention || 0}</span>
                  <span className="text-gray-500 text-sm ml-1">({pieData?.abstention || 0}%)</span>
                  <span className="text-gray-600 text-xs ml-2">{pieData?.tantiemes?.abstention || 0} t.</span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-100 flex justify-between">
                <span className="text-xs text-gray-500">Total: {pieData?.total || 0} vote{(pieData?.total || 0) > 1 ? 's' : ''}</span>
                <span className="text-xs font-medium text-[#00D3A8]">{pieData?.tantiemes?.total || 0} tantièmes</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Vote Results Summary Component with Pie Chart
const VoteResultsSummary = ({ resolutions, votingResults, totalVoters, userRole, activeResolutionId }) => {
  // Find active resolution first, then fall back to last voted resolution
  const activeResolution = resolutions.find(r => r.id === activeResolutionId);
  const lastVotedResolution = resolutions.filter(r => r.resultat !== 'En attente').slice(-1)[0];
  const currentResolution = activeResolution || lastVotedResolution || resolutions[0];
  const apiResults = currentResolution ? votingResults[currentResolution.id] : null;
  
  // Extract counts from the API response structure: { results: { pour: { count, tantiemes }, ... } }
  const results = apiResults?.results || {};
  const pour = results?.pour?.count || 0;
  const contre = results?.contre?.count || 0;
  const abstention = results?.abstention?.count || 0;
  const voted = pour + contre + abstention;
  const nonVoters = Math.max(0, totalVoters - voted);
  const total = Math.max(voted + nonVoters, 1);
  
  // Tantièmes
  const pourTant = results?.pour?.tantiemes || 0;
  const contreTant = results?.contre?.tantiemes || 0;
  const abstTant = results?.abstention?.tantiemes || 0;
  const totalTant = pourTant + contreTant + abstTant;
  
  // Percentages
  const pourPct = Math.round((pour / total) * 100) || 0;
  const contrePct = Math.round((contre / total) * 100) || 0;
  const abstPct = Math.round((abstention / total) * 100) || 0;
  const nonVotersPct = Math.round((nonVoters / total) * 100) || 0;

  // SVG Donut Chart
  const DonutChart = () => {
    const size = 160;
    const strokeWidth = 24;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    
    let offset = 0;
    const segments = [
      { color: '#22c55e', pct: pourPct, label: 'Pour' },
      { color: '#ef4444', pct: contrePct, label: 'Contre' },
      { color: '#9ca3af', pct: abstPct, label: 'Abstention' },
      { color: '#d1d5db', pct: nonVotersPct, label: 'N&apos;ont pas voté' }
    ].filter(s => s.pct > 0);

    return (
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          {segments.map((segment, idx) => {
            const strokeDasharray = (segment.pct / 100) * circumference;
            const strokeDashoffset = -offset;
            offset += strokeDasharray;
            
            return (
              <circle
                key={idx}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${strokeDasharray} ${circumference}`}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-500"
              />
            );
          })}
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-800">{voted}</p>
            <p className="text-xs text-gray-500">votes</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <h3 className="font-semibold text-gray-800 mb-4">Résumé des votes</h3>
      
      {currentResolution && (
        <div className="mb-3">
          <p className="text-xs text-gray-500">
            Résolution {currentResolution.ordre || currentResolution.numero}
          </p>
          <p className="text-sm font-medium text-gray-700 truncate">
            {currentResolution.titre}
          </p>
          {activeResolution && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              Vote en cours
            </span>
          )}
        </div>
      )}
      
      {/* Donut Chart */}
      <div className="flex justify-center mb-4">
        <DonutChart />
      </div>
      
      {/* Legend with tantièmes */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-sm text-gray-700">Pour</span>
          </div>
          <div className="text-right">
            <span className="font-bold text-green-600">{pour}</span>
            <span className="text-gray-400 text-xs ml-1">({pourPct}%)</span>
            <span className="text-green-600 text-xs ml-1 font-medium">{pourTant}t</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-sm text-gray-700">Contre</span>
          </div>
          <div className="text-right">
            <span className="font-bold text-red-600">{contre}</span>
            <span className="text-gray-400 text-xs ml-1">({contrePct}%)</span>
            <span className="text-red-600 text-xs ml-1 font-medium">{contreTant}t</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-700">Abstention</span>
          </div>
          <div className="text-right">
            <span className="font-bold text-gray-600">{abstention}</span>
            <span className="text-gray-400 text-xs ml-1">({abstPct}%)</span>
            <span className="text-gray-600 text-xs ml-1 font-medium">{abstTant}t</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between border-t border-gray-100 pt-2 mt-2">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-200"></div>
            <span className="text-sm text-gray-500">{"N'ont pas voté"}</span>
          </div>
          <div className="text-right">
            <span className="font-bold text-gray-400">{nonVoters}</span>
            <span className="text-gray-400 text-xs ml-1">({nonVotersPct}%)</span>
          </div>
        </div>
        
        {/* Total tantièmes */}
        <div className="flex items-center justify-between border-t border-[#00D3A8] pt-2 mt-2">
          <span className="text-sm font-medium text-[#00D3A8]">Total tantièmes votants</span>
          <span className="font-bold text-[#00D3A8]">{totalTant}</span>
        </div>
      </div>
      
      {/* Total */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total participants</span>
          <span className="font-semibold text-gray-800">{totalVoters}</span>
        </div>
      </div>
      
      {/* Gestionnaire note */}
      {userRole === 'gestionnaire' && nonVoters > 0 && (
        <div className="mt-3 p-2 bg-amber-50 rounded-lg">
          <p className="text-xs text-amber-700">
            {"💡"} {nonVoters} participant{nonVoters > 1 ? "s n'ont" : " n'a"} pas encore voté. 
            {"Vous pouvez voter pour eux s'ils se manifestent avant la clôture."}
          </p>
        </div>
      )}
    </div>
  );
};

export default AGMeetingRoom;
